/**
 * Audio — WAV and MP3 playback, metadata and lossless trimming (§20).
 *
 * §20 draws two firm lines and this handler sits exactly on them:
 *
 * - **Playback is delegated, never bundled.** The handler decodes *nothing*; it
 *   reads the container's metadata and hands the bytes to the platform's own
 *   `<audio>` element. No patented decoder ships in the app.
 * - **Editing is stream copy only.** The single edit is trimming, and it is
 *   done by copying whole sample frames (WAV) or whole MPEG frames (MP3). There
 *   is no re-encoding, so a trim is lossless and involves no encoder — which is
 *   the only kind of media editing §20 allows.
 *
 * Only these two formats are registered, and only because both have a genuine
 * lossless trim. The catalog rates the whole media family `basic-edit`; §19
 * forbids advertising that without a handler that can save, so the formats
 * whose trim is not yet implemented are left unregistered and honestly
 * unsupported rather than opened with a disabled promise.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { parseWav, trimWav } from './wav.js';
import { parseMp3, trimMp3 } from './mp3.js';

export type MediaKind = 'audio';

export interface MediaTrim {
  readonly startSec: number;
  readonly endSec: number;
}

export interface AudioModel {
  readonly kind: MediaKind;
  readonly formatId: string;
  /** MIME type for the `<audio>` element's source. */
  readonly mimeType: string;
  readonly durationSec: number;
  readonly sampleRate: number | null;
  readonly channels: number | null;
  readonly bitrateKbps: number | null;
  readonly tags: Readonly<Record<string, string>>;
  /** True when this stream can be trimmed losslessly. */
  readonly trimmable: boolean;
  /** Pending trim, applied at export. Null means the whole file. */
  readonly trim: MediaTrim | null;
}

const MIME_BY_ID: Readonly<Record<string, string>> = {
  wav: 'audio/wav', mp3: 'audio/mpeg',
};

/* ------------------------------------------------------------------ *
 * reading
 * ------------------------------------------------------------------ */

function readModel(source: FileSource): AudioModel {
  const mimeType = MIME_BY_ID[source.formatId] ?? 'application/octet-stream';

  if (source.formatId === 'wav') {
    const info = parseWav(source.bytes);
    return {
      kind: 'audio', formatId: 'wav', mimeType,
      durationSec: info.durationSec,
      sampleRate: info.format.sampleRate,
      channels: info.format.channels,
      bitrateKbps: Math.round(info.format.byteRate * 8 / 1000) || null,
      tags: info.tags,
      trimmable: info.losslessTrimmable,
      trim: null,
    };
  }

  const info = parseMp3(source.bytes);
  return {
    kind: 'audio', formatId: 'mp3', mimeType,
    durationSec: info.durationSec,
    sampleRate: info.sampleRate,
    channels: info.channels,
    bitrateKbps: info.bitrateKbps,
    tags: info.tags,
    trimmable: info.durationSec > 0,
    trim: null,
  };
}

function firstContent(model: AudioModel): unknown {
  return {
    formatId: model.formatId,
    mimeType: model.mimeType,
    durationSec: model.durationSec,
    tags: model.tags,
    sampleRate: model.sampleRate,
    channels: model.channels,
    bitrateKbps: model.bitrateKbps,
  };
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const audioHandler: FormatHandler<AudioModel> = {
  id: 'audio',
  formatIds: ['wav', 'mp3'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const model = readModel(source);
    return {
      kind: 'media',
      unitCount: undefined,
      unitKey: undefined,
      firstContent: firstContent(model),
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<AudioModel>> {
    throwIfAborted(ctx.signal);
    const model = readModel(source);
    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (model.durationSec <= 0) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: 'The audio stream could not be measured; playback is still attempted through the platform decoder.',
      });
    }
    // §20: whether this file actually plays is a question only the platform's
    // decoder can answer, and the viewer asks it at runtime. The handler states
    // plainly that playback is delegated.
    warnings.push({
      messageKey: 'status.limitedSupport',
      severity: 'info',
      detail: 'Audio is played by the platform’s own decoder. If this device lacks the codec, playback is declined rather than faked (§20).',
    });
    if (!model.trimmable) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'info',
        detail: 'This stream cannot be trimmed losslessly, so it opens for playback only.',
      });
    }
    if (source.sizeBytes > 100 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large media file (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model,
      readOnly: ctx.assessment.forceReadOnly || !model.trimmable,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: AudioModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    if (!model.trim) {
      // No edit: hand back the original bytes unchanged.
      return { bytes: source.bytes, suggestedName: source.filename, formatId: source.formatId, dropped: [] };
    }
    if (!model.trimmable) {
      throw new HandlerError('error.featuresUnsupported', 'this stream cannot be trimmed losslessly');
    }

    const { startSec, endSec } = model.trim;
    const bytes = source.formatId === 'wav'
      ? trimWav(source.bytes, startSec, endSec)
      : trimMp3(source.bytes, startSec, endSec);

    return {
      bytes,
      suggestedName: trimmedName(source.filename),
      formatId: source.formatId,
      // A trim drops audio outside the range by design; that is the edit, not a
      // silent loss, so it is not reported as dropped-feature.
      dropped: [],
    };
  },

  async validateExport(
    bytes: Uint8Array, model: AudioModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const info = model.formatId === 'wav' ? parseWav(bytes) : parseMp3(bytes);
      reopened = true;

      const actual = info.durationSec;
      if (model.trim) {
        const expected = model.trim.endSec - model.trim.startSec;
        // Frame snapping means the duration lands near, not exactly on, the
        // requested range; a whole extra frame is the tolerance.
        const tolerance = Math.max(0.1, (model.sampleRate ? 1152 / model.sampleRate : 0.05) * 2);
        if (Math.abs(actual - expected) > tolerance) {
          issues.push(`trimmed duration ${actual.toFixed(3)}s is not within ${tolerance.toFixed(3)}s of the requested ${expected.toFixed(3)}s`);
        }
      }
      // The tags must survive a trim, since a trim rewrites the container.
      for (const key of ['title', 'artist'] as const) {
        if (model.tags[key] && info.tags[key] !== model.tags[key]) {
          issues.push(`${key} tag was lost in the trim (${model.tags[key]} → ${info.tags[key] ?? '∅'})`);
        }
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the trimmed audio: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

/** Queue a trim. Nothing is written until export (§13). */
export function setTrim(model: AudioModel, startSec: number, endSec: number): AudioModel {
  const start = Math.max(0, Math.min(model.durationSec, startSec));
  const end = Math.max(0, Math.min(model.durationSec, endSec));
  // A trim covering the whole file is not an edit.
  if (start <= 0 && end >= model.durationSec) return { ...model, trim: null };
  return { ...model, trim: { startSec: start, endSec: end } };
}

/** A display string for the format's tags — "Artist — Title" when present. */
export function mediaHeadline(model: AudioModel): string {
  const { title, artist } = model.tags;
  if (title && artist) return `${artist} — ${title}`;
  return title ?? artist ?? '';
}

function trimmedName(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0) return `${filename}-trimmed`;
  return `${filename.slice(0, dot)}-trimmed${filename.slice(dot)}`;
}
